import QRCode from "qrcode";
import { BOOK_CLUB_QR_DATA_URL } from "./bookClubQr";

const CARD_SANS = '"PingFang SC","Microsoft YaHei",sans-serif';

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    // data URL 不要设 crossOrigin，否则部分环境加载失败
    if (!String(src).startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function wrapLines(ctx, text, maxWidth, maxLines) {
  const chars = Array.from(String(text || ""));
  const lines = [];
  let line = "";
  for (const ch of chars) {
    const next = line + ch;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = ch;
      if (lines.length >= maxLines) break;
    } else {
      line = next;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (lines.length === maxLines && chars.length > lines.join("").length) {
    const last = lines[maxLines - 1];
    lines[maxLines - 1] = last.slice(0, Math.max(0, last.length - 1)) + "…";
  }
  return lines;
}

function prettyDate(raw) {
  const m = String(raw || "").match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw || "";
  const week = ["日", "一", "二", "三", "四", "五", "六"];
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00`);
  const w = Number.isNaN(d.getTime()) ? "" : `周${week[d.getDay()]}`;
  return `${m[2]}.${m[3]} · ${w}`.trim();
}

/** 生成高清黑白二维码（带静区），便于微信长按识别 */
async function makeQrDataUrl(text) {
  const payload = String(text || "").trim();
  if (!payload) return "";
  try {
    return await QRCode.toDataURL(payload, {
      errorCorrectionLevel: "M",
      type: "image/png",
      margin: 2,
      width: 512,
      color: { dark: "#000000", light: "#ffffff" },
    });
  } catch {
    return "";
  }
}

function drawQrSharp(ctx, qrImage, x, y, size) {
  if (!qrImage) return;
  const pad = Math.round(size * 0.08);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x - pad, y - pad, size + pad * 2, size + pad * 2);
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 2;
  ctx.strokeRect(x - pad, y - pad, size + pad * 2, size + pad * 2);
  // 关闭平滑，避免码点糊成一片导致扫不出
  const prevSmooth = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qrImage, x, y, size, size);
  ctx.imageSmoothingEnabled = prevSmooth;
}

/**
 * 绘制感恩打卡分享卡片（参考打卡海报：上图下文）
 */
export function paintGratitudeCard(
  canvas,
  {
    items = [],
    authorName = "",
    authorAvatar,
    checkinDay = 1,
    postDate = "",
    qrImage,
  } = {}
) {
  const w = 900;
  const h = 1280;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // 上半：深蓝渐变 + 山峦
  const topH = 700;
  const sky = ctx.createLinearGradient(0, 0, 0, topH);
  sky.addColorStop(0, "#1a3a6b");
  sky.addColorStop(0.55, "#2a5a8f");
  sky.addColorStop(1, "#5a8fb8");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, topH);

  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.beginPath();
  ctx.moveTo(0, topH);
  ctx.lineTo(0, topH - 120);
  ctx.quadraticCurveTo(w * 0.35, topH - 220, w * 0.55, topH - 100);
  ctx.quadraticCurveTo(w * 0.75, topH - 20, w, topH - 80);
  ctx.lineTo(w, topH);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = "rgba(20,50,90,0.35)";
  ctx.beginPath();
  ctx.moveTo(0, topH);
  ctx.lineTo(0, topH - 60);
  ctx.quadraticCurveTo(w * 0.4, topH - 140, w, topH - 40);
  ctx.lineTo(w, topH);
  ctx.closePath();
  ctx.fill();

  // 日期角标
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = `500 26px ${CARD_SANS}`;
  const dateLabel = prettyDate(postDate);
  ctx.fillText(dateLabel, w - 56 - ctx.measureText(dateLabel).width, 56);

  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.font = `700 80px ${CARD_SANS}`;
  ctx.fillText("“", 56, 130);

  ctx.fillStyle = "#ffffff";
  ctx.font = `500 34px ${CARD_SANS}`;
  const list = (items || []).filter(Boolean).slice(0, 8);
  let y = 170;
  const maxW = w - 112;
  list.forEach((it, idx) => {
    const prefix = `${idx + 1}. `;
    const lines = wrapLines(ctx, prefix + it, maxW, 3);
    lines.forEach((ln) => {
      if (y > topH - 110) return;
      ctx.fillText(ln, 56, y);
      y += 46;
    });
    y += 10;
  });

  ctx.font = `600 40px ${CARD_SANS}`;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(`累计打卡 ${checkinDay || 1} 天`, 56, topH - 48);

  // 下半白底
  const cardY = topH - 20;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, cardY, w, h - cardY);

  const ax = 56;
  const ay = cardY + 56;
  const ar = 52;
  ctx.save();
  ctx.beginPath();
  ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (authorAvatar) {
    ctx.drawImage(authorAvatar, ax, ay, ar * 2, ar * 2);
  } else {
    ctx.fillStyle = "#fde68a";
    ctx.fillRect(ax, ay, ar * 2, ar * 2);
    ctx.fillStyle = "#92400e";
    ctx.font = `600 40px ${CARD_SANS}`;
    const letter = (authorName || "?").slice(0, 1);
    ctx.fillText(letter, ax + ar - ctx.measureText(letter).width / 2, ay + ar + 14);
  }
  ctx.restore();

  ctx.fillStyle = "#1f2937";
  ctx.font = `600 36px ${CARD_SANS}`;
  ctx.fillText(authorName || "会员", ax + ar * 2 + 24, ay + 44);
  ctx.fillStyle = "#6b7280";
  ctx.font = `400 24px ${CARD_SANS}`;
  ctx.fillText("感恩留言板 · 每日打卡", ax + ar * 2 + 24, ay + 82);

  // 二维码：加大 + 白边静区 + 关闭平滑，便于微信长按识别
  const qrSize = 220;
  const qx = w - 56 - qrSize;
  const qy = cardY + 48;
  if (qrImage) {
    drawQrSharp(ctx, qrImage, qx, qy, qrSize);
  } else {
    ctx.fillStyle = "#f3f4f6";
    ctx.fillRect(qx, qy, qrSize, qrSize);
  }
  ctx.fillStyle = "#6b7280";
  ctx.font = `500 22px ${CARD_SANS}`;
  const tip = "长按识别二维码";
  ctx.fillText(tip, qx + (qrSize - ctx.measureText(tip).width) / 2, qy + qrSize + 36);

  ctx.fillStyle = "#d1d5db";
  ctx.font = `400 18px ${CARD_SANS}`;
  const foot = "婚恋有道 · 感恩留言板";
  ctx.fillText(foot, (w - ctx.measureText(foot).width) / 2, h - 40);
}

export async function paintGratitudeCardAsync(canvas, opts = {}) {
  const pageUrl =
    opts.pageUrl ||
    (typeof window !== "undefined"
      ? `${window.location.origin}/book-club`
      : "");

  let qrDataUrl = await makeQrDataUrl(pageUrl);
  if (!qrDataUrl) {
    // 回退站点内嵌二维码 / 静态文件
    qrDataUrl = BOOK_CLUB_QR_DATA_URL;
  }

  const [avatar, qr] = await Promise.all([
    loadImage(opts.authorAvatarUrl || ""),
    loadImage(qrDataUrl).then(async (img) => {
      if (img) return img;
      return loadImage("/book-club-qr.png");
    }),
  ]);

  paintGratitudeCard(canvas, {
    ...opts,
    authorAvatar: avatar,
    qrImage: qr,
  });
}
